"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-[background-color,border-color,color,box-shadow,opacity,scale,translate] duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.96]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/85 hover:shadow-md",
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/85 hover:shadow-md",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:shadow-md focus-visible:ring-destructive/20",
        danger:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:shadow-md focus-visible:ring-destructive/20",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:shadow-sm",
        secondary:
          "bg-transparent text-secondary-foreground border border-border shadow-xs hover:bg-muted/50 hover:shadow-sm",
        ghost:
          "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        success:
          "bg-success text-success-foreground shadow-sm hover:bg-success/90 hover:shadow-md",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
        md: "h-9 px-4 py-2 has-[>svg]:px-3",
        lg: "h-10 rounded-lg px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/**
 * A loading button paints its label transparent and overlays the spinner, so
 * `currentColor` inside it is transparent too. Each variant therefore names the
 * colour its spinner should use explicitly.
 */
const spinnerColorByVariant: Record<
  NonNullable<VariantProps<typeof buttonVariants>["variant"]>,
  string
> = {
  default: "text-primary-foreground",
  primary: "text-primary-foreground",
  destructive: "text-destructive-foreground",
  danger: "text-destructive-foreground",
  outline: "text-foreground",
  secondary: "text-secondary-foreground",
  ghost: "text-foreground",
  link: "text-primary",
  success: "text-success-foreground",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconAfter?: React.ReactNode;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      icon,
      iconAfter,
      fullWidth = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : "button";
    const isDisabled = disabled || loading;

    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size }),
          fullWidth && "w-full",
          // Without an icon there is no slot to borrow, so the label keeps its
          // space and is painted transparent while the spinner is overlaid.
          // Gated on motion-safe: globals.css clamps animations to 0.01ms under
          // `prefers-reduced-motion`, so a blanked label plus a frozen arc reads
          // as broken rather than busy. Reduced motion keeps the label readable
          // and drops the overlay; `aria-busy` still announces the state.
          !asChild && loading && !icon && "relative motion-safe:text-transparent",
          className
        )}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {/* A loading button occupies exactly the same box as an idle one.
            Replacing the label with "Loading..." resized the button mid-click
            and slid every sibling control in the row — the reason action rows
            jumped as soon as you clicked one.

            Nothing is ever added to or removed from the flow: with an icon the
            spinner is overlaid on the icon's own box (`size-full`, so a
            size-3.5 icon yields a size-3.5 spinner); without one it is overlaid
            on the whole button. Children are never wrapped — the size variants
            match on `:has(> svg)`, and an extra element would silently change
            the button's horizontal padding. */}
        {asChild ? (
          /* `asChild` hands rendering to the caller's own element via Radix
             Slot, which requires exactly ONE child (`React.Children.only`).
             Decorations would be extra children and throw, so the caller owns
             its own icons — pass their element straight through. */
          children
        ) : (
          <>
            {loading && <span className="sr-only">Loading</span>}
            {loading && !icon && (
              <>
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-0 flex items-center justify-center",
                    "motion-reduce:hidden",
                    spinnerColorByVariant[variant ?? "default"]
                  )}
                >
                  <Loader2 className="size-4 animate-spin" />
                </span>
              </>
            )}
            {icon && (
              <span
                className={cn(
                  "relative inline-flex flex-shrink-0",
                  // `invisible` keeps the icon's exact box; the overlay opts
                  // back in with `visible`, since visibility inherits.
                  // motion-safe only: under reduced motion the spinner cannot
                  // spin, so a frozen arc replacing the icon reads as broken.
                  // Reduced motion keeps the real icon and drops the overlay.
                  loading && "motion-safe:invisible"
                )}
              >
                {icon}
                {loading && (
                  <span
                    aria-hidden="true"
                    className="visible absolute inset-0 flex items-center justify-center motion-reduce:hidden"
                  >
                    <Loader2 className="size-full animate-spin" />
                  </span>
                )}
              </span>
            )}
            {children}
            {iconAfter && <span className="flex-shrink-0">{iconAfter}</span>}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
